import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Utensils, Heart } from "lucide-react";
import { API } from "../constants";
import { RestaurantMiniCard } from "../components/RestaurantMiniCard";

export const DashboardPage = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Filters
  const [showMine, setShowMine] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showAll, setShowAll] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRestaurants();
    fetchFavorites();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const res = await axios.get(`${API}/restaurants`);
      setRestaurants(res.data);
    } catch (err) {
      toast.error("Error al cargar restaurantes");
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavorites(res.data);
    } catch (err) {
      console.error("Error fetching favorites");
    }
  };

  // Filter by search query
  const searchFiltered = restaurants.filter(r => {
    return r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.cuisine_type.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Apply filters
  const filteredRestaurants = searchFiltered.filter(r => {
    // Si solo favoritos está activo (con Todos)
    if (showAll && showFavorites) {
      return favorites.includes(r.id);
    }
    if (showAll) {
      return true;
    }
    if (showMine && showFavorites) {
      // Mis restaurantes que son favoritos
      return r.is_owner === true && favorites.includes(r.id);
    }
    if (showMine) {
      // Solo mis restaurantes
      return r.is_owner === true;
    }
    return true;
  });

  const handleMineClick = () => {
    if (showMine) {
      // Si ya está activo, desactivar y volver a Todos
      setShowMine(false);
      setShowFavorites(false);
      setShowAll(true);
    } else {
      // Activar Mis Restaurantes
      setShowMine(true);
      setShowFavorites(false);
      setShowAll(false);
    }
  };

  const handleFavoritesClick = () => {
    // Funciona tanto con Mis Restaurantes como con Todos
    setShowFavorites(!showFavorites);
  };

  const handleAllClick = () => {
    setShowAll(true);
    setShowMine(false);
    // No resetear showFavorites para permitir Todos + Favoritos
  };

  return (
    <div className="min-h-screen pb-24" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-nav p-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-amber-500 mb-3">Where 2GO</h1>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar restaurantes..."
                className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-4xl mx-auto">
        {/* Filter Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {/* Mis Restaurantes */}
            <button
              onClick={handleMineClick}
              className={`font-semibold transition-colors ${
                showMine ? "text-amber-500" : "text-zinc-400"
              }`}
              data-testid="filter-mine-btn"
            >
              Mis Restaurantes
            </button>
            {/* Corazón Favoritos - funciona con Mis Restaurantes y con Todos */}
            <button
              onClick={handleFavoritesClick}
              className="transition-colors"
              data-testid="filter-favorites-btn"
            >
              <Heart 
                size={18} 
                className={
                  showFavorites 
                    ? "fill-red-500 text-red-500" 
                    : (showMine || showAll)
                      ? "text-zinc-400 fill-none hover:text-red-400" 
                      : "text-zinc-400 fill-none"
                } 
              />
            </button>
          </div>
          {/* Todos - alineado derecha */}
          <button
            onClick={handleAllClick}
            className={`font-semibold transition-colors ${
              showAll ? "text-amber-500" : "text-zinc-400"
            }`}
            data-testid="filter-all-btn"
          >
            Todos
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton aspect-[4/3] rounded-2xl" />
            ))}
          </div>
        ) : restaurants.length === 0 ? (
          <div className="empty-state">
            <Utensils className="empty-state-icon" />
            <h3 className="text-xl font-semibold mb-2">Sin restaurantes</h3>
            <p className="text-zinc-400 mb-4">Añade tu primer restaurante favorito</p>
            <button
              onClick={() => navigate("/add")}
              className="px-6 py-3 bg-amber-500 text-black font-semibold rounded-xl btn-glow"
              data-testid="add-first-restaurant-btn"
            >
              Añadir Restaurante
            </button>
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="empty-state">
            {showFavorites && showAll ? (
              <>
                <Heart className="empty-state-icon" />
                <h3 className="text-xl font-semibold mb-2">Sin favoritos</h3>
                <p className="text-zinc-400 mb-4">No tienes restaurantes marcados como favoritos</p>
              </>
            ) : showFavorites && showMine ? (
              <>
                <Heart className="empty-state-icon" />
                <h3 className="text-xl font-semibold mb-2">Sin favoritos propios</h3>
                <p className="text-zinc-400 mb-4">No tienes restaurantes propios marcados como favoritos</p>
              </>
            ) : showMine ? (
              <>
                <Utensils className="empty-state-icon" />
                <h3 className="text-xl font-semibold mb-2">Sin restaurantes propios</h3>
                <p className="text-zinc-400 mb-4">No has creado ningún restaurante</p>
              </>
            ) : (
              <>
                <Utensils className="empty-state-icon" />
                <h3 className="text-xl font-semibold mb-2">Sin resultados</h3>
                <p className="text-zinc-400 mb-4">No se encontraron restaurantes</p>
              </>
            )}
            <button
              onClick={() => {
                setShowAll(true);
                setShowMine(false);
                setShowFavorites(false);
                setSearchQuery("");
              }}
              className="px-6 py-3 bg-amber-500 text-black font-semibold rounded-xl btn-glow"
              data-testid="empty-state-view-all-btn"
            >
              Ver todos
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredRestaurants.map((restaurant) => (
              <RestaurantMiniCard
                key={restaurant.id}
                restaurant={restaurant}
                onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                isFavorite={favorites.includes(restaurant.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
